import { PageShell } from '@homeproved/com/feature-page-shell';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import {
  apiFactory,
  getDehydratedState,
  ReviewsApiFactory,
  SectorsApiFactory,
} from '@homeproved/shared/data-access';
import { useTranslation } from 'react-i18next';
import { useLocalizedRoutes } from '@homeproved/com/feature-localized-routes';
import { useSectors, prefetch, SectorPage } from '@homeproved/shared/feature-sectors';
import { ALGOLIA_INDEX, searchClient } from '@homeproved/com/feature-search';
import { SearchSection } from '@homeproved/shared/feature-company-search';
import { getResultsState } from '@homeproved/shared/feature-algolia';

const Index = ({ resultsState }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { data: sectors, isLoading, error } = useSectors();
  const [sectorNotFound, setSectorNotFound] = useState(false);

  const { getPath } = useLocalizedRoutes();

  const { sector } = router.query;
  const fetchedSector = sectors?.find((s) => s.data.slug.data[router.locale] === sector);

  useEffect(() => {
    if (!fetchedSector) {
      setSectorNotFound(true);
      router.replace(getPath('/sectors')).then();
    }
  }, [fetchedSector, getPath, router]);

  if (sectorNotFound) {
    return <>{t('app.com.pages.sectors.subSector.redirect')}</>;
  }

  if (isLoading) return <>{t('app.pro.pages.profile.activitypicker.loading')}</>;
  if (error) return <>{error.toString()}</>;
  if (!sectors) return <>{t('app.pro.pages.profile.activitypicker.invalid')}</>;

  return (
    fetchedSector != null && (
      <PageShell fullWidth={true} innerPadding={false} padding={false}>
        <SectorPage
          sector={fetchedSector.data}
          getPath={getPath}
          searchClient={searchClient}
          indexName={ALGOLIA_INDEX.COMPANIES}
          resultsState={resultsState}
        />
      </PageShell>
    )
  );
};

export async function getServerSideProps({ locale, query }) {
  let sector = null;
  let totalReviews = null;
  const sectorsApi = apiFactory(locale, SectorsApiFactory);
  await sectorsApi
    .apiSectorSectorGet(query.sector)
    .then((result) => {
      sector = result;
    })
    .catch((error) => {
      console.log(error);
    });

  const reviewsApi = apiFactory(locale, ReviewsApiFactory);
  await reviewsApi
    .apiReviewCounterGet()
    .then((result) => {
      totalReviews = result;
    })
    .catch((error) => {
      console.log(error);
    });

  const dehydratedState = await getDehydratedState(prefetch.sector(locale));
  const resultsState = await getResultsState(
    <SearchSection
      getPath={() => '/'}
      isMobile={false}
      sectorName={query.sector}
      page={query.page?.toString()}
      query={query.query?.toString()}
    />,
    {
      searchClient,
      indexName: ALGOLIA_INDEX.COMPANIES,
    }
  );

  return {
    props: {
      dehydratedState,
      resultsState,
      meta: {
        title: 'meta.titles.subSectorPage',
        description: 'meta.descriptions.subSectorPage',
        variables: {
          '%SECTOR%': sector?.data?.data?.name ?? '',
          '%X_REVIEWS%': totalReviews?.data?.count ?? '',
        },
      },
    },
  };
}

export default Index;
